/**
 * Invitar a una persona: correo, nombre, empresa y perfil. Antes de enviar se confirma, porque sale un
 * correo real. El formato se valida al salir del campo y todo de nuevo al enviar; el servidor decide.
 */
import { useId, useState, type FormEvent } from "react";
import { mensajeDeAccion, type Institucion, type Perfil, type Usuario } from "@/shared/api";
import { t } from "@/shared/i18n";
import { Aviso, Boton, Campo, Dialogo, Selector } from "@/shared/ui";
import { useInvitar } from "../api/consultas";
import { normalizarCorreo, validarInvitacion, type ErroresInvitacion } from "../model/usuarios";
import type { PrellenadoInvitacion } from "./Lista";
import { ElegirPerfil } from "./Piezas";
import estilos from "./Usuarios.module.css";

const i = t.invitar;

export function FormularioInvitacion({
  instituciones,
  prellenado,
  alInvitar,
}: {
  readonly instituciones: readonly Institucion[];
  /** Correo y nombre ya escritos (al invitar de nuevo a un usuario dado de baja). */
  readonly prellenado?: PrellenadoInvitacion | null;
  readonly alInvitar: (u: Usuario) => void;
}) {
  const invitar = useInvitar();
  const [correo, setCorreo] = useState(prellenado?.correo ?? "");
  const [nombre, setNombre] = useState(prellenado?.nombre ?? "");
  const [institucion, setInstitucion] = useState("");
  const [perfil, setPerfil] = useState<Perfil>("operaciones");
  const [errores, setErrores] = useState<ErroresInvitacion>({});
  const [confirmando, setConfirmando] = useState(false);
  const idErrorEmpresa = useId();

  const datos = { correo, nombre, institucion, perfil };
  const empresa = instituciones.find((x) => x.institucion === institucion);

  const revisar = (e: FormEvent) => {
    e.preventDefault();
    const encontrados = validarInvitacion(datos);
    setErrores(encontrados);
    if (Object.keys(encontrados).length === 0) {
      invitar.reset();
      setConfirmando(true);
    }
  };

  const enviar = () => {
    invitar.mutate(
      { correo: normalizarCorreo(correo), nombre: nombre.trim(), institucion, perfil },
      {
        onSuccess: (u) => {
          setConfirmando(false);
          alInvitar(u);
        },
      },
    );
  };

  return (
    <form className={estilos.formulario} onSubmit={revisar} noValidate>
      <Campo
        rotulo={i.correo}
        type="email"
        autoComplete="off"
        spellCheck={false}
        maxLength={254}
        value={correo}
        error={errores.correo ? i.errores.correo : null}
        onChange={(e) => {
          setCorreo(e.target.value);
        }}
        onBlur={() => {
          if (correo) setErrores((x) => ({ ...x, correo: validarInvitacion(datos).correo }));
        }}
      />
      <Campo
        rotulo={i.nombre}
        autoComplete="off"
        maxLength={120}
        value={nombre}
        error={errores.nombre ? i.errores.nombre : null}
        onChange={(e) => {
          setNombre(e.target.value);
        }}
      />
      <div>
        <Selector
          rotulo={i.empresa}
          ayuda={i.empresaAyuda}
          value={institucion}
          aria-invalid={errores.empresa ? true : undefined}
          aria-describedby={errores.empresa ? idErrorEmpresa : undefined}
          opciones={[
            { valor: "", texto: i.elegirEmpresa },
            ...instituciones.map((x) => ({ valor: x.institucion, texto: `${x.nombre} (${x.institucion})` })),
          ]}
          onChange={(e) => {
            setInstitucion(e.target.value);
            setErrores((x) => ({ ...x, empresa: undefined }));
          }}
        />
        {errores.empresa && (
          <span id={idErrorEmpresa} className={estilos.error} role="alert">
            {i.errores.empresa}
          </span>
        )}
      </div>
      <ElegirPerfil leyenda={i.perfil} valor={perfil} alCambiar={setPerfil} />
      <div className={estilos.pie}>
        <Boton type="submit" variante="principal">
          {i.enviar}
        </Boton>
      </div>

      <Dialogo
        abierto={confirmando}
        titulo={i.confirmarTitulo}
        descripcion={i.confirmarTexto(normalizarCorreo(correo), empresa?.nombre ?? institucion)}
        alCerrar={() => {
          setConfirmando(false);
        }}
      >
        {invitar.isError && (
          <Aviso tono="grave" rol="alert">
            {mensajeDeAccion(invitar.error)}
          </Aviso>
        )}
        <div className={estilos.pie}>
          <Boton
            onClick={() => {
              setConfirmando(false);
            }}
          >
            {t.general.cancelar}
          </Boton>
          <Boton variante="principal" disabled={invitar.isPending} onClick={enviar}>
            {invitar.isPending ? i.enviando : i.confirmar}
          </Boton>
        </div>
      </Dialogo>
    </form>
  );
}
